import { normalizeId } from './ids';

/**
 * Grouping a collection's cards by the Discover bundle they came from
 * (keepr-api KPR-1130; epic KPE-035). The API says which cards each installed
 * package holds (`cardIds`, the core's first, then each add-on's); the library
 * knows the rows it can show. This joins them.
 *
 * Pure: no fetching, no search. The caller passes the rows it wants grouped
 * (already narrowed by the search), and a group with none left is dropped —
 * "a group with no match is hidden".
 */

/**
 * `groupBundleRows(packages, rows)`:
 *   packages  the API's rows (`getCollectionPackages`)
 *   rows      the library's member rows, in the order to show them (`{ id, … }`)
 * → `{ groups, grouped }`
 *   groups   one per package that has at least one row, in the API's order:
 *            `{ bundle, core: rows[], addOns: [{ key, name, rows }], count }`
 *   grouped  Set of the row ids that landed in a group
 *
 * A row is in ONE group (the first package that names it). A row a package
 * names but the library does not show (not visible to this person, deleted
 * since) is simply not there. A card the API gives under an add-on is listed
 * under that add-on, whatever the rows' order; the rows inside each part keep
 * the order they came in.
 */
export function groupBundleRows(packages, rows) {
	const placed = new Set();
	const groups = [];
	(Array.isArray(packages) ? packages : []).forEach((bundle) => {
		const ids = new Set((bundle?.cardIds || []).map(normalizeId));
		const own = (rows || []).filter(row => ids.has(normalizeId(row.id)) && !placed.has(normalizeId(row.id)));
		if (!own.length) return;
		const addOns = [];
		const inAddOn = new Set();
		((bundle.addOns && bundle.addOns.installed) || []).forEach((addOn) => {
			const mine = new Set((addOn.cardIds || []).map(normalizeId));
			const rowsOf = own.filter(row => mine.has(normalizeId(row.id)) && !inAddOn.has(normalizeId(row.id)));
			rowsOf.forEach(row => inAddOn.add(normalizeId(row.id)));
			if (rowsOf.length) addOns.push({ key: addOn.key, name: addOn.name || addOn.key, rows: rowsOf });
		});
		const core = own.filter(row => !inAddOn.has(normalizeId(row.id)));
		own.forEach(row => placed.add(normalizeId(row.id)));
		groups.push({ bundle, core, addOns, count: own.length });
	});
	return { groups, grouped: placed };
}

/**
 * The element sets a bundle brought, by id → the bundle's name, for the
 * *From <bundle>* line in the Element sets list. Read from every package, not
 * only the groups that show: a set belongs to its bundle whether or not a
 * search is hiding the bundle's cards.
 */
export function elementSetBundles(packages) {
	const out = new Map();
	(Array.isArray(packages) ? packages : []).forEach((bundle) => {
		(bundle?.elementSetIds || []).forEach((id) => {
			const key = normalizeId(id);
			if (key && !out.has(key)) out.set(key, bundle.name || bundle.key);
		});
	});
	return out;
}

/** The chip words for a bundle's version: `v5`. */
export function versionChip(bundle) {
	const n = Number(bundle?.version);
	return Number.isFinite(n) && n > 0 ? `v${n}` : null;
}
